import dayjs from "dayjs"
import { useCallback, useSyncExternalStore } from "react"

import { IsoDate } from "#customTypes/types"

type LocalStorage = {
  isLoggedIn: boolean
  loginExpiresAt: IsoDate
  failedPinLoginAttempts: number
  pinLockoutUntilMs: number
}

const USER_SETTINGS_STORAGE_KEY = "jarvis-user-settings"
const DEFAULT_LOCAL_STORAGE: LocalStorage = {
  isLoggedIn: false,
  // 6 AM today
  loginExpiresAt: dayjs().startOf("day").hour(5).toISOString(),
  failedPinLoginAttempts: 0,
  pinLockoutUntilMs: 0,
}

// Memoize since parse builds a new object every time
let settingsSingletonRaw: string | null = null
let settingsSingleton: LocalStorage = DEFAULT_LOCAL_STORAGE

const parseStoredSettings = (settings: string | null): LocalStorage => {
  if (!settings) {
    return DEFAULT_LOCAL_STORAGE
  }

  const parsed = JSON.parse(settings) as Partial<LocalStorage>
  return {
    ...DEFAULT_LOCAL_STORAGE,
    ...parsed,
    pinLockoutUntilMs: parsed.pinLockoutUntilMs ?? 0,
  }
}

export const getJarvisLocalStorage = (): LocalStorage => {
  const settings = localStorage.getItem(USER_SETTINGS_STORAGE_KEY)

  if (settings === settingsSingletonRaw) return settingsSingleton

  settingsSingletonRaw = settings
  settingsSingleton = parseStoredSettings(settings)
  return settingsSingleton
}

// Get initial settings synchronously to avoid flash
const getInitialSettings = (): LocalStorage => {
  try {
    const settings = localStorage.getItem(USER_SETTINGS_STORAGE_KEY)
    return parseStoredSettings(settings)
  } catch {
    return DEFAULT_LOCAL_STORAGE
  }
}

const subscribe = (callback: () => void): (() => void) => {
  window.addEventListener("storage", callback)
  return () => {
    window.removeEventListener("storage", callback)
  }
}

const persistSettings = (nextSettings: LocalStorage) => {
  localStorage.setItem(USER_SETTINGS_STORAGE_KEY, JSON.stringify(nextSettings))
  settingsSingletonRaw = localStorage.getItem(USER_SETTINGS_STORAGE_KEY)
  settingsSingleton = nextSettings
  window.dispatchEvent(new Event("storage"))
}

const useLocalStorage = (): [
  LocalStorage,
  (updates: Partial<LocalStorage>) => void,
] => {
  const settings = useSyncExternalStore(
    subscribe,
    getJarvisLocalStorage,
    getInitialSettings
  )

  const updateSettings = useCallback((updates: Partial<LocalStorage>) => {
    const currentSettings = getJarvisLocalStorage()
    persistSettings({
      ...currentSettings,
      ...updates,
    })
  }, [])

  return [settings, updateSettings]
}

export default useLocalStorage
